<template>
  <div class="card h-100 tags-donuts-widget">
    <!-- <div class="card-header">
      <h4 class="card-title">{{ widgetTitle }}</h4>
    </div> -->
    <WidgetHeader
      :title="widgetTitle"
      :swiperIsActive="!swiperHasReachedEnd"
      :onBack="canGoBack ? back : null"
    />
    <WidgetBody
      v-if="!singleStatItem"
      :id="id"
      :onSwiperInitCallback="onSwiperInit"
      :initialSlideIndex="widgetSliderState.index"
      :viewMode="viewMode"
      @next-transition-start="updateSliderIndex"
      @prev-transition-start="updateSliderIndex"
      @prev-transition-end="onPrevTransitionEnd"
    >
      <template #slide-0>
        <div class="row d-flex justify-content-center">
          <div
            v-for="tag in tagsWithData"
            :key="tag.name"
            class="tag-wrapper my-5"
            :class="donutColSizeClasses"
          >
            <div v-if="tag.name && !shouldHideDonutForTag(tag)">
              <TagDonutChart
                v-bind="propsForTag(tag)"
                :chartType="tag.npsData ? 'enps' : 'tag'"
                :onOpenTagDetails="openTagDetails"
                :theme="theme"
              />
            </div>
            <div
              v-if="shouldDisplayAlert"
              :class="`${!shouldHideDonutForTag(tag) ? 'mt-10' : ''}`"
            >
              <div v-if="tagShouldDisplayWithAlert(tag)">
                <p
                  v-for="(paragraph, j) in alertTextsForTag(tag)"
                  :key="j"
                  class="alert-paragraph fs-5"
                >
                  {{ paragraph }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!shouldDisplayAlert">
          <div v-if="tagsWithData.length" class="row">
            <div class="col col-md-12 col-lg-12 my-3 mt-5">
              <div class="fs-5">
                {{ description }}
              </div>
            </div>
          </div>
          <div v-else class="row">
            <div class="col col-md-12 col-lg-12 my-3 mt-5">
              <p class="info-title d-flex align-items-center m-auto fs-5">
                <i class="bi bi-info-circle-fill text-primary fs-3 me-3"></i>
                {{ $t('widgetTagsDonuts.notEnoughResponses') }}
              </p>
            </div>
          </div>
        </div>
      </template>
      <template #slide-1>
        <TagDetails
          v-if="currentTag"
          :tag="currentTag"
          :resultsPerPerson="
            shouldShowResultsPerPerson &&
            tagsResultsPerPerson &&
            tagsResultsPerPerson[currentTag.name]
          "
          :shouldShowResultsPerPerson="shouldShowResultsPerPerson"
          :theme="theme"
          :onSelectedTabChange="onSelectedTabChange"
          :selectedTab="selectedTabForDetails"
          :shouldBeZeroToHundred="tagShouldBeZeroToHundred(currentTag)"
        />
      </template>
    </WidgetBody>
    <div v-else class="card-body widget-slider">
      <TagDetails
        v-if="currentTag"
        :tag="currentTag"
        :resultsPerPerson="
          shouldShowResultsPerPerson &&
          tagsResultsPerPerson &&
          tagsResultsPerPerson[currentTag.name]
        "
        :shouldShowResultsPerPerson="shouldShowResultsPerPerson"
        :theme="theme"
        :onSelectedTabChange="onSelectedTabChange"
        :selectedTab="selectedTabForDetails"
      />
    </div>
  </div>
</template>

<script>
// import NPSChart from './subcomponents/NPSChart.vue'
// import TagChart from './subcomponents/TagChart.vue'
import TagDonutChart from './subcomponents/TagDonutChart.vue'
import TagDetails from './subcomponents/TagDetails.vue'
import { requestTypes } from '@/constants/request-types'
import { errorTypes } from '@/constants/error-types'
import commonWidgetProps from '@/components/mixins/common-widget-props'
import WidgetHeader from '@/components/widgets/shared/WidgetHeader.vue'
import WidgetBody from '@/components/widgets/shared/WidgetBody.vue'

export const tagsDonutsWidgetInformation = ({ colData }) => {
  const tags = (colData.tags || []).reduce((acc, tag) => {
    acc[tag] = {}
    return acc
  }, {})
  const requests = {
    [requestTypes.SURVEY_DETAILS]: {},
    [requestTypes.TAGS]: { filter: tags },
    [requestTypes.TREND_TAGS]: { filter: tags }
  }
  return {
    requests
  }
}

export const tagsDonutsWidgetSkeleton = ({ colData }) => {
  return {
    header: {
      height: 2
    },
    body: [
      {
        cols: colData.tags.map(() => {
          return { height: 14, type: 'circle' }
        })
      },
      {
        cols: [{ height: 4 }]
      }
    ]
  }
}

const TagsDonutsWidget = {
  name: 'TagsDonutsWidget',
  props: {
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    tags: {
      type: Object,
      default: () => {}
    },
    trendTags: {
      type: Object,
      default: () => {}
    },
    updateWidgetRequests: {
      type: Function,
      default: () => null
    },
    tagsResultsPerPerson: {
      type: Object,
      default: () => {}
    },
    singleStatItem: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    TagDetails,
    TagDonutChart,
    WidgetHeader,
    WidgetBody
  },
  mixins: [commonWidgetProps],
  data() {
    return {
      currentTag: null,
      selectedTabForDetails: 'overall',
      swiperHasReachedEnd: false
    }
  },
  mounted() {
    if (this.noTagHasValue) {
      this.deregisterWidget({
        id: this.id,
        info: {
          reason: errorTypes.NOT_ENOUGH_DATA,
          hideWidgetWhenNoSurveyResults: true
        },
        groupName: this.selectedGroupName
      })
    } else if (this.singleStatItem) {
      const tagWithProps = {
        ...this.tags[this.singleStatItem.name],
        name: this.singleStatItem.name,
        averages: this.trendTags[this.singleStatItem.name]?.averages,
        regressionValues:
          this.trendTags[this.singleStatItem.name]?.regressionValues
      }
      this.openTagDetails(tagWithProps)
      this.swiperHasReachedEnd = true
    } else if (
      this.widgetSliderState.index > 0 &&
      this.widgetSliderState.info
    ) {
      this.currentTag = {
        ...this.widgetSliderState.info.currentTag,
        averages:
          this.trendTags[this.widgetSliderState.info.currentTag.name]?.averages,
        regressionValues:
          this.trendTags[this.widgetSliderState.info.currentTag.name]
            ?.regressionValues
      }
      this.selectedTabForDetails = this.widgetSliderState.info.selectedTabName
      this.swiperHasReachedEnd = true
    }
  },
  methods: {
    alertTextsForTag(tag) {
      if (tag.value) {
        const messages = tag.messages
        const value = tag.value
        let res = 'high'
        if (value < 7) {
          res = 'medium'
        }
        if (value < 4) {
          res = 'low'
        }
        const msg = this.$tr(messages, `results.${res}.alert`)
        if (msg.includes('\n')) {
          return msg.split('\n')
        } else {
          return [msg]
        }
      } else {
        return ''
      }
    },
    back() {
      if (this.singleStatItem) {
        this.singleStatItem.onBack()
      } else {
        this.swiperInstance.slidePrev()
        this.updateSliderIndex(this.swiperInstance)
      }
      this.currentTag = null
    },
    onPrevTransitionEnd(swiper) {},
    onPrevTransitionStart(swiper) {
      this.currentTag = null
    },
    onSwiperInit(swiper) {
      this.swiperInstance = swiper
      this.swiperInstance.on('slideChangeTransitionEnd', () => {
        if (swiper.activeIndex === 1) {
          this.swiperHasReachedEnd = true
        } else {
          this.swiperHasReachedEnd = false
        }
      })
    },
    openTagDetails(tag) {
      if (this.shouldShowResultsPerPerson) {
        this.updateWidgetRequests({
          mode: 'add',
          id: this.id,
          requests: {
            [requestTypes.TAGS_RESULTS_PER_PERSON]: {
              params: {
                [tag.name]: {}
              }
            }
          }
        })
      }
      this.currentTag = tag
      if (this.swiperInstance) {
        this.swiperInstance.slideNext()
      }
    },
    propsForTag(tag) {
      const messages = this.surveyDetails.details.messages
      const tagTitle = this.$tr(messages, `tags.${tag.name}.title`)
      const tagDescription = tag.npsData
        ? this.$t('widgetTagsDonuts.tooltip.enps_description', {
            tagName: tagTitle,
            groupName: this.selectedGroupTitle
          })
        : this.$t('widgetTagsDonuts.tooltip.value', {
            tagName: tagTitle,
            groupName: this.selectedGroupTitle
          })

      const res = {
        value: tag.value,
        tagTitle: this.$tr(messages, `tags.${tag.name}.title`),
        // description: this.$tr(messages, `tags.${tag.name}.description`),
        tagDescription: tagDescription,
        tag: {
          ...tag,
          averages: this.trendTags[tag.name]?.averages || [],
          regressionValues: this.trendTags[tag.name]?.regressionValues || []
        },
        tagShouldBeZeroToHundred: this.tagShouldBeZeroToHundred(tag),
        tagShouldBePercentage: this.tagShouldBePercentage(tag),
        shouldShowResultsPerPerson: this.shouldShowResultsPerPerson,
        userIsAdminForCurrentGroupOrSuperAdmin:
          this.userIsGroupAdmin || this.userIsPulsAdmin
      }
      return res
    },
    shouldHideDonutForTag(tag) {
      if (this.tagShouldDisplayWithAlert(tag)) {
        return this.tagSettingsMap[tag.name].shouldHideDonut
      } else {
        return false
      }
    },
    tagShouldDisplayWithAlert(tag) {
      return this.tagSettingsMap[tag.name]?.alerts?.alert || false
    },
    tagShouldBeZeroToHundred(tag) {
      if (this.tagSettingsMap[tag.name].hasOwnProperty('zeroToHundred')) {
        return this.tagSettingsMap[tag.name].zeroToHundred
      } else {
        const isEnps = tag.name === 'enps'
        return (this.data.zeroToHundred && !isEnps) || false
      }
    },
    tagShouldBePercentage(tag) {
      if (this.tagSettingsMap[tag.name].hasOwnProperty('percentage')) {
        return this.tagSettingsMap[tag.name].percentage
      } else {
        return this.data.percentage || false
      }
    },
    updateSliderIndex(swiper) {
      this.updateWidgetSliderState({
        id: this.id,
        index: swiper.activeIndex,
        info: { currentTag: this.currentTag }
      })
    },
    valueForTag(tagName) {
      const tag = this.tags[tagName]
      return tag?.value || '-'
    },
    onSelectedTabChange(selectedTabName) {
      this.selectedTabForDetails = selectedTabName
      this.updateWidgetSliderState({
        id: this.id,
        index: this.swiperInstance?.activeIndex || 0,
        info: { currentTag: this.currentTag, selectedTabName }
      })
      setTimeout(() => {
        if (this.swiperInstance?.params) {
          this.swiperInstance.updateAutoHeight(200)
        }
      }, 25)
    }
  },
  computed: {
    canGoBack() {
      if (this.singleStatItem || this.currentTag !== null) {
        return true
      }
      return false
    },
    shouldDisplayAlert() {
      // at least one tag should have alert displayed instead of description
      return (
        this.data.tagSettings &&
        this.data.tagSettings.some((s) => s.alerts?.alert)
      )
    },
    tagSettingsMap() {
      return this.tagsWithData.reduce((res, tag) => {
        res[tag.name] =
          this.data.tagSettings?.find((s) => s.tagName === tag.name) || {}
        return res
      }, {})
    },
    noTagHasValue() {
      // should show even if t.value === 0
      return Object.values(this.tags || {})
        .filter((tagName) => tagName !== 'participation')
        .every((t) => !t.hasOwnProperty('value') || t.value === undefined)
      // return Object.values(this.tags || {}).every((t) => !t?.value)
    },
    description() {
      if (this.tagsWithData.length > 1) {
        return this.$t('widgetTagsDonuts.description.all', {
          groupName: this.selectedGroupTitle
        })
      } else {
        const res = this.tagsWithData[0]
        if (res.name === 'enps') {
          return this.$t('widgetTagsDonuts.tooltip.enps_description')
        } else {
          return ''
        }
      }
    },
    donutColSizeClasses() {
      let smClass, mdClass, lgClass, xlClass
      if (this.tagsWithData.length > 1) {
        smClass = 'col-sm-6'
        mdClass = `col-md-${12 / this.tagsWithData.length}`
        lgClass = `col-lg-${12 / this.tagsWithData.length}`
        xlClass = `col-xl-${12 / this.tagsWithData.length}`
      }
      return `
        ${smClass || 'col-sm-8'}
        ${mdClass || 'col-md-7'}
        ${lgClass || 'col-lg-6'}
        ${xlClass || 'col-xl-6'}
      `
    },

    groupTitle() {
      if (this.selectedGroupName === this.organizationName) {
        return this.$t('widgetTagsDonuts.title.company', {
          organizationName: this.organizationFullName
        })
      } else {
        return this.selectedGroupTitle
      }
    },
    shouldShowResultsPerPerson() {
      return (
        (!this.selectedGroup?.numberOfUsers ||
          this.selectedGroup?.numberOfUsers > 1) &&
        this.data.showResultsPerPerson?.enabled
      )
    },
    tagsWithData() {
      return this.data.tags.map((tagName) => {
        const tag = this.tags[tagName]
        return {
          ...tag,
          name: tagName
        }
      })
    },
    widgetTitle() {
      if (this.currentTag) {
        if (this.singleStatItem) {
          return this.singleStatItem.group.title
            ? `${this.singleStatItem.group.title} - ${this.$tr(this.currentTag.messages, 'title')}`
            : `${this.$tr(this.singleStatItem.group.messages, 'title')} - ${this.$tr(this.currentTag.messages, 'title')}`
        }
        return `${this.$tr(this.currentTag.messages, 'title')}`
      } else {
        return ''
      }
    }
  }
}

export default TagsDonutsWidget
</script>

<style>
.tag-wrapper {
  max-width: 300px;
}
</style>
